/**
 * Module de gestion du login
 */

import { AUTH_CONFIG } from './auth-config.js';
import { trackEvent, sanitizeRedirect, handleNetworkError, setButtonLoading } from './auth-helpers.js';

// Fonction pour afficher les erreurs de login
function showLoginError(errorCode, errorDivId = 'login-error') {
    const errorDiv = document.getElementById(errorDivId);
    const allErrors = errorDiv.querySelectorAll('[id^="error-"]');
    
    // Cacher tous les messages
    allErrors.forEach(el => el.style.display = 'none');
    
    // Afficher le bon message selon le code
    let targetId;
    if (errorCode === 'EMPTY_FIELDS') {
        targetId = 'error-empty';
    } else if (errorCode === 'WRONG_PASSWORD') {
        targetId = 'error-wrong-password';
    } else if (errorCode === 'NOT_FOUND') {
        targetId = 'error-not-found';
    } else if (errorCode === 'TOO_MANY_TRIES') {
        targetId = 'error-too-many-tries';
    } else if (errorCode === 'ACCOUNT_BANNED' || errorCode.startsWith('ACCOUNT_BANNED:')) {
        targetId = 'error-banned';
    } else if (errorCode === 'ACCOUNT_DELETED') {
        targetId = 'error-deleted';
    } else if (errorCode === 'ACCOUNT_DELETED_FOREVER') {
        targetId = 'error-deleted-forever';
    } else if (errorCode === 'ACCOUNT_PENDING_CONFIRMATION') {
        targetId = 'error-pending-confirmation';
    } else if (errorCode.startsWith('USERNAME_CHANGED:')) {
        const username = errorCode.split(':')[1];
        const el = document.getElementById('error-username-changed');
        const template = el.innerHTML || 'L\'identifiant ce compte est désormais : <i>Shinobi{username}</i>';
        el.innerHTML = template.replace('{username}', username);
        targetId = 'error-username-changed';
    } else {
        targetId = 'error-network';
    }
    
    const targetError = document.getElementById(targetId);
    if (targetError) {
        targetError.style.display = 'block';
        errorDiv.style.display = 'block';
    }
}

// Gestion du switch entre login et register
export function initModeSwitch() {
    document.querySelectorAll('.mode-btn').forEach(btn => {
        btn.addEventListener('click', function() {
            const mode = this.dataset.mode;
            
            // Update URL
            const url = new URL(window.location);
            url.searchParams.set('mode', mode);
            window.history.pushState({}, '', url);
            
            // Update buttons
            document.querySelectorAll('.mode-btn').forEach(b => b.classList.remove('active'));
            this.classList.add('active');
            
            // Update forms
            document.querySelectorAll('.auth-form').forEach(f => f.classList.remove('active'));
            document.querySelector('.auth-form.' + mode + '-section').classList.add('active');
            
            // Si on passe en mode login, on cache le fond village et on affiche le fond par défaut
            const villageBackground = document.getElementById('villageBackground');
            if (villageBackground) {
                if (mode === 'login') {
                    villageBackground.classList.remove('village-selected');
                } else if (mode === 'register') {
                    // Si on a déjà sélectionné un village, on l'affiche
                    const selectedVillage = document.querySelector('input[name="village"]:checked');
                    if (selectedVillage) {
                        villageBackground.classList.add('village-selected');
                    }
                }
            }
            
            // Clear login errors when switching modes
            const loginError = document.getElementById('login-error');
            if (loginError) {
                loginError.style.display = 'none';
            }
            
            // Analytics
            trackEvent('MODE_SWITCH', { new_mode: mode });
        });
    });
}

// Initialiser le formulaire de login
export function initLoginForm(options = {}) {
    const {
        formId = 'login-form',
        submitBtnId = 'login-submit', 
        errorDivId = 'login-error',
        apiEndpoint = AUTH_CONFIG.API.LOGIN,
        redirectType = 'LOGIN',
        defaultRedirect = 'index.php?page=jeu',
        analyticsPrefix = 'LOGIN'
    } = options;
    const form = document.getElementById(formId);
    if (!form) return;

    form.addEventListener('submit', async function(e) {
        e.preventDefault();
        
        const errorDiv = document.getElementById(errorDivId);
        const formData = new FormData(this);
        
        // Validation côté client
        const login = formData.get('login');
        const password = formData.get('pass');
        
        if (!login || !password || login.trim() === '' || password.trim() === '') {
            showLoginError('EMPTY_FIELDS', errorDivId);
            return;
        }
        
        // UI Loading state
        setButtonLoading(submitBtnId);
        errorDiv.style.display = 'none';
        
        // Analytics
        trackEvent(`${analyticsPrefix}_ATTEMPT`);
        
        try {
            const response = await fetch(apiEndpoint, {
                method: 'POST',
                body: formData
            });
            
            const data = await response.json();
            
            if (data.success) {
                // Analytics success
                trackEvent(`${analyticsPrefix}_SUCCESS`);
                
                // Redirect sécurisé
                const redirectUrl = sanitizeRedirect(data.redirect, redirectType, defaultRedirect);
                window.location.href = redirectUrl;
            } else {
                // Afficher l'erreur sécurisée basée sur le code
                showLoginError(data.error, errorDivId);
                
                // Analytics error
                trackEvent(`${analyticsPrefix}_ERROR`, { error_type: data.error });
            }
        } catch (error) {
            // Erreur réseau ou JSON parsing seulement
            showLoginError('NETWORK_ERROR', errorDivId);
            handleNetworkError(error);
        } finally {
            // Reset UI
            setButtonLoading(submitBtnId, false);
        }
    });
}